import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import { getAppVersions, getVarietyVersionGates, updateVarietyVersionGates } from '../Api';
import VarietyVersionGatesDto, { VarietyVersionGateDto } from '../models/dtos/VarietyVersionGatesDto';
import AppVersionDto from '../models/dtos/AppVersionDto';
import {
    AppVersionNameAutocomplete,
    BuildNumberAutocomplete,
    useAppVersionsForOsTypes,
} from '../utils/appVersionFields';

const PLAYER_STATS_GATE_ID = 'player_stats';
const PLAYER_STATS_PARAM_T_ID = 11;
const ANDROID_OS = 'android';

function playerStatsGate(): VarietyVersionGateDto {
    return {
        id: PLAYER_STATS_GATE_ID,
        enabled: true,
        paramTIds: [PLAYER_STATS_PARAM_T_ID],
        minBuildNumber: 3600,
        maxBuildNumber: null,
        minAppVersion: null,
        maxAppVersion: null,
    };
}

function pickPlayerStatsGate(gates: VarietyVersionGateDto[] | undefined): VarietyVersionGateDto {
    const found = gates?.find(g => g.id === PLAYER_STATS_GATE_ID);
    if (found == null) return playerStatsGate();
    return {
        ...found,
        paramTIds: found.paramTIds?.length ? found.paramTIds : [PLAYER_STATS_PARAM_T_ID],
    };
}

export default function VarietyVersionGatesPage() {
    const [loading, setLoading] = React.useState(true);
    const [globalEnabled, setGlobalEnabled] = React.useState(false);
    const [gateEnabled, setGateEnabled] = React.useState(true);
    const [minBuildNumber, setMinBuildNumber] = React.useState<number | null>(3600);
    const [maxBuildNumber, setMaxBuildNumber] = React.useState<number | null>(null);
    const [minAppVersion, setMinAppVersion] = React.useState<string | null>(null);
    const [maxAppVersion, setMaxAppVersion] = React.useState<string | null>(null);
    const [androidVersions, setAndroidVersions] = React.useState<AppVersionDto[]>([]);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');

    const { versions: registeredVersions, loading: versionsLoading } = useAppVersionsForOsTypes([ANDROID_OS]);

    const applyDto = (dto: VarietyVersionGatesDto | null) => {
        if (dto == null) return;
        setGlobalEnabled(dto.globalEnabled ?? false);
        const gate = pickPlayerStatsGate(dto.gates);
        setGateEnabled(gate.enabled);
        setMinBuildNumber(gate.minBuildNumber ?? null);
        setMaxBuildNumber(gate.maxBuildNumber ?? null);
        setMinAppVersion(gate.minAppVersion ?? null);
        setMaxAppVersion(gate.maxAppVersion ?? null);
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const [dto, versions] = await Promise.all([
                getVarietyVersionGates(),
                getAppVersions(ANDROID_OS),
            ]);
            if (!cancelled) {
                applyDto(dto);
                setAndroidVersions(versions ?? []);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const versionsForSelectors = registeredVersions.length > 0 ? registeredVersions : androidVersions;

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        const payload: VarietyVersionGatesDto = {
            version: 1,
            globalEnabled,
            gates: [
                {
                    ...playerStatsGate(),
                    enabled: gateEnabled,
                    minBuildNumber,
                    maxBuildNumber,
                    minAppVersion,
                    maxAppVersion,
                },
            ],
        };
        const saveError = await updateVarietyVersionGates(payload);
        if (saveError != null) {
            openDialog('Error', saveError);
            return;
        }
        const dto = await getVarietyVersionGates();
        applyDto(dto);
        openDialog('Saved', 'Player Stats version gate was saved.');
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">Player Stats — app version gate</Typography>
            <Typography variant="body2" color="text.secondary">
                Hide the Player Stats variety tab (game type id {PLAYER_STATS_PARAM_T_ID}) when the app build is
                outside the configured range. Build numbers and version names are chosen from versions registered in analytics.
            </Typography>
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={globalEnabled}
                                onChange={e => setGlobalEnabled(e.target.checked)}
                            />
                        }
                        label="Global enabled"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={gateEnabled}
                                onChange={e => setGateEnabled(e.target.checked)}
                            />
                        }
                        label="Player Stats gate enabled"
                    />
                    <Grid container spacing={2} sx={{ maxWidth: 720 }}>
                        <Grid item xs={12} sm={6}>
                            <BuildNumberAutocomplete
                                label="Min build number (visible from)"
                                value={minBuildNumber}
                                onChange={setMinBuildNumber}
                                versions={versionsForSelectors}
                                loading={versionsLoading}
                                peerMax={maxBuildNumber}
                                helperText="VERSION_CODE from analytics registry (Android)."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <BuildNumberAutocomplete
                                label="Max build number (optional)"
                                value={maxBuildNumber}
                                onChange={setMaxBuildNumber}
                                versions={versionsForSelectors}
                                loading={versionsLoading}
                                peerMin={minBuildNumber}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AppVersionNameAutocomplete
                                label="Min app version name (optional)"
                                value={minAppVersion}
                                onChange={setMinAppVersion}
                                versions={versionsForSelectors}
                                loading={versionsLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AppVersionNameAutocomplete
                                label="Max app version name (optional)"
                                value={maxAppVersion}
                                onChange={setMaxAppVersion}
                                versions={versionsForSelectors}
                                loading={versionsLoading}
                            />
                        </Grid>
                    </Grid>
                    <Box>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Box>
                </>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" component="div">{dialogMsg}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>OK</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
